'use client';
import React, { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, AlertTriangle, BarChart2, Layers } from 'lucide-react';
import { useMarketData, useIndex } from '@/contexts/MarketDataContext';
import { type MarketSession } from '@/lib/india-market';

const SESSION_BADGE: Record<MarketSession, { label: string; cls: string } | null> = {
  PRE_OPEN:   { label: 'PRE-MARKET', cls: 'bg-warning/10 text-warning border border-warning/30' },
  OPEN:       null,
  POST_CLOSE: { label: 'POST-CLOSE', cls: 'bg-info/10 text-info border border-info/30' },
  CLOSED:     { label: 'LAST CLOSE', cls: 'bg-muted text-muted-foreground border border-border' },
};

interface MetricsBentoGridProps {
  selectedIndex?: string;
  onIndexSelect?: (symbol: string) => void;
}

export default function MetricsBentoGrid({ selectedIndex, onIndexSelect }: MetricsBentoGridProps) {
  const [isClient, setIsClient] = useState(false);
  useEffect(() => { setIsClient(true); }, []);

  const { isConnected, marketSession } = useMarketData();
  const nifty          = useIndex('NIFTY 50');
  const bankNifty      = useIndex('BANKNIFTY');
  const midcpNifty     = useIndex('MIDCPNIFTY');
  const sensex         = useIndex('SENSEX');
  const finNifty       = useIndex('FINNIFTY');
  const midcap150      = useIndex('NIFTYMIDCAP150');

  const metrics = [
    {
      id: 'metric-daypnl',
      label: "Today\'s P&L",
      value: '+₹4,287.50',
      sub: 'Realised + Unrealised',
      change: '+2.34%',
      positive: true,
      hero: true,
      icon: TrendingUp,
      detail: '3 positions open',
    },
    {
      id: 'metric-portfolio',
      label: 'Portfolio Value',
      value: '₹1,83,450.00',
      sub: 'Holdings + Positions',
      change: '+₹12,340 this week',
      positive: true,
      hero: false,
      icon: BarChart2,
      detail: '12 holdings',
    },
    {
      id: 'metric-margin',
      label: 'Available Margin',
      value: '₹42,180.00',
      sub: 'Free cash for trading',
      change: '68% of total',
      positive: true,
      hero: false,
      icon: Layers,
      detail: 'Used: ₹19,820',
    },
    {
      id: 'metric-orders',
      label: 'Open Orders',
      value: '3',
      sub: 'Pending execution',
      change: '1 at risk',
      positive: false,
      warning: true,
      hero: false,
      icon: AlertTriangle,
      detail: 'SL order below trigger',
    },
  ];

  // Helper: format index change string — always shows change + pct
  function fmtChange(idx: { change: number; pct: number } | null): string {
    if (!idx) return '—';
    const sign = idx.change >= 0 ? '+' : '';
    return `${sign}${idx.change.toFixed(2)} (${sign}${idx.pct.toFixed(2)}%)`;
  }

  function fmtValue(idx: { value: number } | null, fallback: string): string {
    if (!isClient) return fallback; // stable SSR value
    if (!idx) return fallback;
    return idx.value.toLocaleString('en-IN', { minimumFractionDigits: 2 });
  }

  function fmtDetail(idx: { high: number; low: number } | null, fallback: string): string {
    if (!isClient) return fallback;
    if (!idx) return fallback;
    return `H: ${idx.high.toLocaleString('en-IN', { maximumFractionDigits: 0 })} | L: ${idx.low.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
  }

  const indexData = [
    {
      id: 'idx-nifty50',
      label: 'NSE NIFTY 50',
      symbol: 'NIFTY 50',
      value: fmtValue(nifty, '23,654.70'),
      change: isClient ? fmtChange(nifty) : '-88.65 (-0.37%)',
      positive: nifty ? nifty.change >= 0 : false,
      detail: fmtDetail(nifty, 'H: 23,780 | L: 23,610'),
    },
    {
      id: 'idx-banknifty',
      label: 'BANKNIFTY',
      symbol: 'BANKNIFTY',
      value: fmtValue(bankNifty, '53,439.40'),
      change: isClient ? fmtChange(bankNifty) : '+216.00 (+0.41%)',
      positive: bankNifty ? bankNifty.change >= 0 : true,
      detail: fmtDetail(bankNifty, 'H: 53,510 | L: 53,150'),
    },
    {
      id: 'idx-midcpnifty',
      label: 'MIDCP NIFTY',
      symbol: 'MIDCPNIFTY',
      value: fmtValue(midcpNifty, '14,351.90'),
      change: isClient ? fmtChange(midcpNifty) : '+107.20 (+0.75%)',
      positive: midcpNifty ? midcpNifty.change >= 0 : true,
      detail: fmtDetail(midcpNifty, 'H: 14,380 | L: 14,220'),
    },
    {
      id: 'idx-sensex',
      label: 'BSE SENSEX',
      symbol: 'SENSEX',
      value: fmtValue(sensex, '75,183.36'),
      change: isClient ? fmtChange(sensex) : '-312.09 (-0.41%)',
      positive: sensex ? sensex.change >= 0 : false,
      detail: fmtDetail(sensex, 'H: 75,620 | L: 75,050'),
    },
    {
      id: 'idx-finnifty',
      label: 'FINNIFTY',
      symbol: 'FINNIFTY',
      value: fmtValue(finNifty, '25,237.20'),
      change: isClient ? fmtChange(finNifty) : '+98.65 (+0.39%)',
      positive: finNifty ? finNifty.change >= 0 : true,
      detail: fmtDetail(finNifty, 'H: 25,290 | L: 25,100'),
    },
    {
      id: 'idx-midcap150',
      label: 'MIDCAP 150',
      symbol: 'NIFTYMIDCAP150',
      value: fmtValue(midcap150, '22,479.15'),
      change: isClient ? fmtChange(midcap150) : '+173.20 (+0.78%)',
      positive: midcap150 ? midcap150.change >= 0 : true,
      detail: fmtDetail(midcap150, 'H: 22,510 | L: 22,280'),
    },
  ];

  return (
    <div className="flex flex-col gap-1.5 lg:gap-2">
      {/* Live indicator */}
      <div className="flex items-center gap-1.5 justify-end -mb-0.5">
        {SESSION_BADGE[marketSession] && (
          <span className={`text-2xs font-semibold px-2 py-0.5 rounded-full ${SESSION_BADGE[marketSession]!.cls}`}>
            {SESSION_BADGE[marketSession]!.label}
          </span>
        )}
        <span className={`w-1.5 h-1.5 rounded-full ${isConnected ? 'bg-positive animate-pulse' : 'bg-muted-foreground'}`} />
        <span className="text-2xs text-muted-foreground">
          {marketSession === 'OPEN' ? 'Live · 0.5s' : isConnected ? 'Connected' : 'Connecting…'}
        </span>
      </div>

      {/* Main metrics row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
        {metrics.map((m) => {
          const IconComp = m.icon;
          const isWarning = (m as any).warning;
          return (
            <div
              key={m.id}
              className={`card-elevated px-2.5 py-1.5 flex items-center gap-2 transition-all duration-200 hover:border-primary/30 cursor-pointer ${
                isWarning
                  ? 'border-warning/30 bg-warning-subtle'
                  : m.hero
                  ? 'border-primary/30 bg-positive-subtle' : ''
              }`}
            >
              <div className={`w-6 h-6 rounded-md flex items-center justify-center shrink-0 ${
                isWarning ? 'bg-warning/10' : m.positive ? 'bg-positive/10' : 'bg-muted'
              }`}>
                <IconComp size={12} className={isWarning ? 'text-warning' : m.positive ? 'text-positive' : 'text-muted-foreground'} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-2xs text-muted-foreground truncate">{m.label}</p>
                <p className={`text-sm font-bold font-tabular leading-tight truncate ${
                  isWarning ? 'text-warning' : m.positive ? 'text-positive' : 'text-foreground'
                }`}>
                  {m.value}
                </p>
              </div>
              <span className={`text-2xs font-semibold font-tabular shrink-0 ${
                isWarning ? 'text-warning' : m.positive ? 'text-positive' : 'text-negative'
              }`}>
                {m.change}
              </span>
            </div>
          );
        })}
      </div>

      {/* Indices row */}
      <div className="grid grid-cols-3 sm:grid-cols-3 lg:grid-cols-6 gap-2 lg:gap-2.5">
        {indexData.map((idx) => (
          <div
            key={idx.id}
            onClick={() => onIndexSelect?.(idx.symbol)}
            className={`card-elevated p-2 flex flex-col gap-1 transition-all duration-200 hover:border-primary/30 hover:shadow-lg cursor-pointer ${
              selectedIndex === idx.symbol ? 'border-primary/50 bg-primary/5 shadow-md' : ''
            }`}
          >
            <span className="text-2xs font-semibold uppercase tracking-widest text-muted-foreground truncate">
              {idx.label}
            </span>
            {/* Session overlay for pre-market / closed */}
            {isClient && marketSession === 'PRE_OPEN' && (
              <span className="text-2xs font-semibold text-warning truncate">Pre-Market</span>
            )}
            {isClient && (marketSession === 'CLOSED' || marketSession === 'POST_CLOSE') && (
              <span className="text-2xs font-semibold text-muted-foreground truncate">Last Close</span>
            )}
            <p className={`text-sm font-bold font-tabular leading-tight ${idx.positive ? 'text-positive' : 'text-negative'}`}>
              {idx.value}
            </p>
            <div className="flex items-center gap-1">
              {idx.positive ? <TrendingUp size={9} className="text-positive shrink-0" /> : <TrendingDown size={9} className="text-negative shrink-0" />}
              <span className={`text-2xs font-semibold font-tabular truncate ${idx.positive ? 'text-positive' : 'text-negative'}`}>
                {idx.change}
              </span>
            </div>
            <p className="text-2xs text-muted-foreground truncate">{idx.detail}</p>
          </div>
        ))}
      </div>
    </div>
  );
}